import { useEffect } from 'react';

/*
    🔥 BLOQUEO DEL SCROLL DE LA PÁGINA

    Lo usan el cajón del sidebar, los modales, el visor de imágenes, el
    confirm y el panel de navegación del header.

    ⚠️ POR QUÉ UN HOOK Y NO UN useEffect EN CADA UNO

    Había CINCO lugares haciendo su propio guardar/restaurar del
    overflow del body. Con dos abiertos a la vez, el restore se pisa:

        1. Se abre el modal   → guarda overflow: ''      y pone hidden
        2. Se abre el sidebar → guarda overflow: 'hidden' y pone hidden
        3. Se cierra el modal → restaura ''  ← el scroll vuelve
        4. …con el sidebar TODAVÍA abierto

    Con un contador compartido, el bloqueo se levanta recién cuando el
    último lo suelta.

    ⚠️ POR QUÉ position: fixed Y NO SOLO overflow: hidden

    En Safari de iPhone, overflow: hidden sobre el body NO frena el
    scroll: el gesto sigue moviendo la página detrás del cajón. Es el
    problema más viejo de los modales en móvil.

    Fijar el body en la posición actual sí lo frena en todos lados. El
    costo es que hay que acordarse de dónde estaba el scroll y
    devolverlo al soltar, que es lo que hace onRestore.

    ⚠️ SE COMPENSA EL ANCHO DE LA BARRA

    Al esconder la barra de scroll, la página se ensancha unos 15 px y
    todo salta hacia la derecha. El padding la reemplaza mientras dura
    el bloqueo.
*/

// 🔥 Contador compartido por todos los que bloquean
let LOCKS = 0;
let SAVED = null;

const onLock = () => {

    LOCKS += 1;

    // 🔥 Ya estaba bloqueado por otro: solo se suma al contador
    if (LOCKS > 1) return;

    const BODY = document.body;
    const HTML = document.documentElement;

    const SCROLL_Y = window.scrollY;

    // ⚠️ Diferencia entre la ventana y el documento = ancho de la barra
    const GAP = window.innerWidth - HTML.clientWidth;

    SAVED = {
        scrollY: SCROLL_Y,
        position: BODY.style.position,
        top: BODY.style.top,
        left: BODY.style.left,
        right: BODY.style.right,
        width: BODY.style.width,
        overflow: BODY.style.overflow,
        paddingRight: BODY.style.paddingRight,
        scrollBehavior: HTML.style.scrollBehavior,
    };

    /*
        ⚠️ EL SCROLL SUAVE SE APAGA MIENTRAS DURA EL BLOQUEO

        Si el proyecto tiene scroll-behavior: smooth, al restaurar la
        posición el navegador la ANIMA: se ve la página deslizándose
        sola después de cerrar el cajón.
    */
    HTML.style.scrollBehavior = 'auto';

    BODY.style.position = 'fixed';
    BODY.style.top = `-${SCROLL_Y}px`;
    BODY.style.left = '0';
    BODY.style.right = '0';
    BODY.style.width = '100%';
    BODY.style.overflow = 'hidden';

    /*
        ⚠️ Con scrollbar-gutter: stable el hueco ya esta reservado, asi que
        agregar padding correria el layout en vez de compensarlo.
    */
    const HAS_GUTTER = typeof CSS !== 'undefined' && CSS.supports?.('scrollbar-gutter', 'stable');

    if (GAP > 0 && !HAS_GUTTER) BODY.style.paddingRight = `${GAP}px`;

    // 🔥 El atributo permite esconder la barra por CSS
    HTML.setAttribute('data-ls-scroll-lock', 'true');
};

const onUnlock = () => {

    LOCKS = Math.max(0, LOCKS - 1);

    // 🔥 Todavía queda alguien bloqueando
    if (LOCKS > 0 || !SAVED) return;

    const BODY = document.body;
    const HTML = document.documentElement;

    BODY.style.position = SAVED.position;
    BODY.style.top = SAVED.top;
    BODY.style.left = SAVED.left;
    BODY.style.right = SAVED.right;
    BODY.style.width = SAVED.width;
    BODY.style.overflow = SAVED.overflow;
    BODY.style.paddingRight = SAVED.paddingRight;

    HTML.removeAttribute('data-ls-scroll-lock');

    /*
        ⚠️ SE DEVUELVE LA POSICIÓN ANTES DE REPONER scroll-behavior

        Al orden inverso, el navegador anima el salto de vuelta y se ve
        la página deslizándose sola.
    */
    window.scrollTo(0, SAVED.scrollY);

    HTML.style.scrollBehavior = SAVED.scrollBehavior;

    SAVED = null;
};

/*
    🔥 HOOK

        useScrollLock(isOpen);

    Bloquea mientras isActive sea true y suelta al cerrarse o al
    desmontarse el componente.

    ⚠️ LA LIMPIEZA CORRE SIEMPRE

    Si el componente se desmonta con el cajón abierto —por una
    navegación, por ejemplo— el bloqueo se suelta igual. Sin eso, la
    página queda sin poder scrollear y no hay forma de recuperarla
    salvo recargando.
*/
export const useScrollLock = (isActive = false) => {

    useEffect(() => {

        if (!isActive) return;

        onLock();

        return onUnlock;

    }, [isActive]);
};

// 🔥 Para casos fuera de un componente. Devuelve la función que suelta.
export const onScrollLock = () => {
    onLock();
    return onUnlock;
};
